import * as React from 'react';

import type {
  UseCheckUserEmailParams,
  UseCheckUserEmailReturn,
} from './useCheckUserEmail.types';

import { checkUserEmail } from '../../api/check-user-email/checkUserEmail';
import {
  EMAIL_CHECK_FAILED_ERROR,
  EMAIL_EXISTS_ERROR,
  isValidEmailFormat,
} from './useCheckUserEmail.utils';

export function useCheckUserEmail(
  email: string,
  { organizationId, enabled }: UseCheckUserEmailParams
): UseCheckUserEmailReturn {
  const [isCheckingEmail, setIsCheckingEmail] = React.useState(false);
  const [emailExistsError, setEmailExistsError] = React.useState<string | null>(
    null
  );
  const abortControllerRef = React.useRef<AbortController | null>(null);

  const cancelPendingCheck = React.useCallback((): void => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const handleEmailBlur = React.useCallback((): void => {
    if (!enabled || !organizationId) return;

    const trimmedEmail = email.trim();
    if (!trimmedEmail || !isValidEmailFormat(trimmedEmail)) return;

    cancelPendingCheck();
    setIsCheckingEmail(true);
    setEmailExistsError(null);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    checkUserEmail({ email: trimmedEmail, organizationId }, controller.signal)
      .then((exists: boolean): void => {
        if (!controller.signal.aborted) {
          setEmailExistsError(exists ? EMAIL_EXISTS_ERROR : null);
          setIsCheckingEmail(false);
        }
      })
      .catch((): void => {
        if (!controller.signal.aborted) {
          setEmailExistsError(EMAIL_CHECK_FAILED_ERROR);
          setIsCheckingEmail(false);
        }
      });
  }, [email, organizationId, enabled, cancelPendingCheck]);

  const handleEmailFocus = React.useCallback((): void => {
    cancelPendingCheck();
    setIsCheckingEmail(false);
    setEmailExistsError(null);
  }, [cancelPendingCheck]);

  const resetEmailCheck = React.useCallback((): void => {
    cancelPendingCheck();
    setIsCheckingEmail(false);
    setEmailExistsError(null);
  }, [cancelPendingCheck]);

  React.useEffect(() => {
    return (): void => {
      cancelPendingCheck();
    };
  }, [cancelPendingCheck]);

  return {
    isCheckingEmail,
    emailExistsError,
    handleEmailBlur,
    handleEmailFocus,
    resetEmailCheck,
  };
}
